@if (debugMode.isDebugMode()) {
  <div class="mock-panel">

    <!-- ── Enabled mock card previews ────────────────────────────────────────── -->

    @if (hasEnabled()) {
      <div class="mock-panel__preview d-flex flex-column gap-3 px-3 py-2">

        <!-- Floating background-job panel (shows all enabled bg-job snapshots) -->
        @if (enabledBgJobMocks().length > 0) {
          <app-background-job-panel />
        }

        <!-- Reasoning steps (outside card grid, rendered as a column) -->
        @for (mock of enabledReasoningMocks(); track mock.stepLabel) {
          <lib-reasoning-step [stepInfo]="mock" [isStreaming]="mock.stepLabel.endsWith('…')" />
        }

        <!-- Tool call hint chips (debug-only, inline row) -->
        @if (enabledToolCallMocks().length > 0) {
          <div class="d-flex flex-wrap gap-2">
            @for (mock of enabledToolCallMocks(); track mock.id) {
              <app-tool-call-hint [toolCall]="mock" />
            }
          </div>
        }

        <!-- Tool cards (rendered via ContentBlockCardComponent) -->
        @if (enabledToolMocks().length > 0) {
          <app-card-grid>
            @for (mock of enabledToolMocks(); track mock.id) {
              <app-content-block-card [kind]="mock.kind" [data]="mock.data" [blockId]="mock.id" />
            }
          </app-card-grid>
        }

        <!-- Map layer cards (kind: map_layer — dedicated MapLayerCardComponent, same as the dispatcher) -->
        @if (enabledMapLayerMocks().length > 0) {
          <app-card-grid>
            @for (mock of enabledMapLayerMocks(); track mock.id) {
              <app-map-layer-card [data]="mock.data" status="completed" />
            }
          </app-card-grid>
        }

        <!-- Plan cards (view "card" — dedicated PlanCardComponent in the grid, same as the dispatcher) -->
        @if (enabledPlanCardMocks().length > 0) {
          <app-card-grid>
            @for (mock of enabledPlanCardMocks(); track mock.id) {
              <app-plan-card class="task-card-full-width" [data]="mock.plan" status="completed" />
            }
          </app-card-grid>
        }

        <!-- Plan progress cards (view "progress" — durable `plan` input fallback, full width) -->
        @for (mock of enabledPlanProgressMocks(); track mock.id) {
          <app-plan-progress-card class="d-block" [plan]="mock.plan" />
        }

        <!-- Evidence progress (debug-only card family — episodes replayed into the panel-local store) -->
        @if (evidenceEnabled()) {
          <app-evidence-progress-card class="d-flex flex-column gap-2" />
        }

        <!-- Geo + workarea hint chips (inline row outside the grid — same as the dispatcher) -->
        @if (enabledGeoChipMocks().length > 0 || enabledWorkareaChipMocks().length > 0) {
          <div class="d-flex flex-wrap gap-2">
            @for (mock of enabledGeoChipMocks(); track mock.id) {
              <app-geo-hint-chip [data]="mock.data" />
            }
            @for (mock of enabledWorkareaChipMocks(); track mock.id) {
              <app-workarea-hint-chip [data]="mock.data" />
            }
          </div>
        }

        <!-- Background job progress cards (full width, outside card-grid) -->
        @for (snap of enabledBgJobMocks(); track snap.block.job_id) {
          <app-task-list-card [block]="snap.block" />
        }

        <!-- HITL cards (full width, each entry as its own card) -->
        @for (mock of enabledHitlMocks(); track mock.display_text) {
          @if (mock.type === 'frontend_tools') {
            <app-hitl-tool-call-card [displayText]="mock.display_text" [items]="mock.items" />
          } @else {
            @for (entry of mock.items; track entry.id) {
              @switch (entry.kind) {
                @case ('boolean') {
                  <app-hitl-confirm-card [entry]="entry" (submitted)="onHitlSubmit($event)" />
                }
                @case ('select') {
                  <app-hitl-selection-card [entry]="entry" (submitted)="onHitlSubmit($event)" />
                }
                @case ('multiselect') {
                  <app-hitl-selection-card [entry]="entry" (submitted)="onHitlSubmit($event)" />
                }
                @default {
                  <app-hitl-form-card [entry]="entry" (submitted)="onHitlSubmit($event)" />
                }
              }
            }
          }
        }

      </div>
    }

    <!-- ── Panel toggle bar ───────────────────────────────────────────────────── -->

    <div class="mock-panel__bar d-flex align-items-center gap-2 px-3 py-1">
      <mat-icon class="mat-icon-sm ai-card__warning-icon">bug_report</mat-icon>
      <span class="mat-font-label-md ai-card__warning-text flex-fill">Mock Cards</span>
      @if (enabledCount() > 0) {
        <span class="mock-panel__badge d-inline-flex align-items-center justify-content-center">{{ enabledCount() }}</span>
      }
      @if (hasEnabled()) {
        <button matIconButton
                class="mat-text-on-surface-variant"
                matTooltip="Disable all mocks"
                (click)="disableAll()">
          <mat-icon>clear_all</mat-icon>
        </button>
      }
      <button matIconButton (click)="togglePanel()" [matTooltip]="isPanelOpen() ? 'Collapse settings' : 'Expand settings'">
        <mat-icon>{{ isPanelOpen() ? 'expand_more' : 'settings' }}</mat-icon>
      </button>
    </div>

    <!-- ── Settings panel (expandable) ──────────────────────────────────────── -->

    @if (isPanelOpen()) {
      <div class="mock-panel__settings d-flex flex-column gap-0">

        <!-- Tool Result Cards -->
        <div class="mock-panel__section">
          <div class="mock-panel__section-header d-flex align-items-center gap-2 px-3 py-1">
            <mat-icon class="mat-icon-xs mat-text-on-surface-variant">build</mat-icon>
            <span class="mat-font-label-md fw-semibold">Tool Result Cards</span>
          </div>
          @for (item of mocks.toolItems; track item.id) {
            <label class="mock-panel__item d-flex align-items-center gap-2 px-4 py-1">
              <mat-checkbox [ngModel]="item.enabled()" (ngModelChange)="item.enabled.set($event)" />
              <span class="mat-font-body-sm">{{ item.label }}</span>
            </label>
          }
        </div>

        <mat-divider />

        <!-- Background Jobs -->
        <div class="mock-panel__section">
          <div class="mock-panel__section-header d-flex align-items-center gap-2 px-3 py-1">
            <mat-icon class="mat-icon-xs mat-text-on-surface-variant">work_history</mat-icon>
            <span class="mat-font-label-md fw-semibold">Background Jobs</span>
          </div>
          @for (item of mocks.bgJobItems; track item.id) {
            <label class="mock-panel__item d-flex align-items-center gap-2 px-4 py-1">
              <mat-checkbox [ngModel]="item.enabled()" (ngModelChange)="item.enabled.set($event)" />
              <span class="mat-font-body-sm">{{ item.label }}</span>
            </label>
          }
        </div>

        <mat-divider />

        <!-- Reasoning Steps -->
        <div class="mock-panel__section">
          <div class="mock-panel__section-header d-flex align-items-center gap-2 px-3 py-1">
            <mat-icon class="mat-icon-xs mat-text-on-surface-variant">psychology</mat-icon>
            <span class="mat-font-label-md fw-semibold">Reasoning Steps</span>
          </div>
          @for (item of mocks.reasoningItems; track item.id) {
            <label class="mock-panel__item d-flex align-items-center gap-2 px-4 py-1">
              <mat-checkbox [ngModel]="item.enabled()" (ngModelChange)="item.enabled.set($event)" />
              <span class="mat-font-body-sm">{{ item.label }}</span>
            </label>
          }
        </div>

        <mat-divider />

        <!-- Map Layer Cards -->
        <div class="mock-panel__section">
          <div class="mock-panel__section-header d-flex align-items-center gap-2 px-3 py-1">
            <mat-icon class="mat-icon-xs mat-text-on-surface-variant">layers</mat-icon>
            <span class="mat-font-label-md fw-semibold">Map Layer Cards</span>
          </div>
          @for (item of mocks.mapLayerItems; track item.id) {
            <label class="mock-panel__item d-flex align-items-center gap-2 px-4 py-1">
              <mat-checkbox [ngModel]="item.enabled()" (ngModelChange)="item.enabled.set($event)" />
              <span class="mat-font-body-sm">{{ item.label }}</span>
            </label>
          }
        </div>

        <mat-divider />

        <!-- Plan Cards -->
        <div class="mock-panel__section">
          <div class="mock-panel__section-header d-flex align-items-center gap-2 px-3 py-1">
            <mat-icon class="mat-icon-xs mat-text-on-surface-variant">account_tree</mat-icon>
            <span class="mat-font-label-md fw-semibold">Plan Cards</span>
          </div>
          @for (item of mocks.planItems; track item.id) {
            <label class="mock-panel__item d-flex align-items-center gap-2 px-4 py-1">
              <mat-checkbox [ngModel]="item.enabled()" (ngModelChange)="item.enabled.set($event)" />
              <span class="mat-font-body-sm">{{ item.label }}</span>
            </label>
          }
        </div>

        <mat-divider />

        <!-- Evidence Progress -->
        <div class="mock-panel__section">
          <div class="mock-panel__section-header d-flex align-items-center gap-2 px-3 py-1">
            <mat-icon class="mat-icon-xs mat-text-on-surface-variant">insights</mat-icon>
            <span class="mat-font-label-md fw-semibold">Evidence Progress</span>
          </div>
          @for (item of mocks.evidenceItems; track item.id) {
            <label class="mock-panel__item d-flex align-items-center gap-2 px-4 py-1">
              <mat-checkbox [ngModel]="item.enabled()" (ngModelChange)="item.enabled.set($event)" />
              <span class="mat-font-body-sm">{{ item.label }}</span>
            </label>
          }
        </div>

        <mat-divider />

        <!-- Geo Hints (chips) -->
        <div class="mock-panel__section">
          <div class="mock-panel__section-header d-flex align-items-center gap-2 px-3 py-1">
            <mat-icon class="mat-icon-xs mat-text-on-surface-variant">place</mat-icon>
            <span class="mat-font-label-md fw-semibold">Geo Hints (chips)</span>
          </div>
          @for (item of mocks.geoChipItems; track item.id) {
            <label class="mock-panel__item d-flex align-items-center gap-2 px-4 py-1">
              <mat-checkbox [ngModel]="item.enabled()" (ngModelChange)="item.enabled.set($event)" />
              <span class="mat-font-body-sm">{{ item.label }}</span>
            </label>
          }
        </div>

        <mat-divider />

        <!-- Workarea Chip -->
        <div class="mock-panel__section">
          <div class="mock-panel__section-header d-flex align-items-center gap-2 px-3 py-1">
            <mat-icon class="mat-icon-xs mat-text-on-surface-variant">draw</mat-icon>
            <span class="mat-font-label-md fw-semibold">Workarea Chip</span>
          </div>
          @for (item of mocks.workareaChipItems; track item.id) {
            <label class="mock-panel__item d-flex align-items-center gap-2 px-4 py-1">
              <mat-checkbox [ngModel]="item.enabled()" (ngModelChange)="item.enabled.set($event)" />
              <span class="mat-font-body-sm">{{ item.label }}</span>
            </label>
          }
        </div>

        <mat-divider />

        <!-- Tool Call Hints -->
        <div class="mock-panel__section">
          <div class="mock-panel__section-header d-flex align-items-center gap-2 px-3 py-1">
            <mat-icon class="mat-icon-xs mat-text-on-surface-variant">call_made</mat-icon>
            <span class="mat-font-label-md fw-semibold">Tool Call Hints</span>
          </div>
          @for (item of mocks.toolCallItems; track item.id) {
            <label class="mock-panel__item d-flex align-items-center gap-2 px-4 py-1">
              <mat-checkbox [ngModel]="item.enabled()" (ngModelChange)="item.enabled.set($event)" />
              <span class="mat-font-body-sm">{{ item.label }}</span>
            </label>
          }
        </div>

        <mat-divider />

        <!-- HITL Interrupts -->
        <div class="mock-panel__section">
          <div class="mock-panel__section-header d-flex align-items-center gap-2 px-3 py-1">
            <mat-icon class="mat-icon-xs mat-text-on-surface-variant">pan_tool</mat-icon>
            <span class="mat-font-label-md fw-semibold">HITL Interrupts</span>
          </div>
          @for (item of mocks.hitlItems; track item.id) {
            <label class="mock-panel__item d-flex align-items-center gap-2 px-4 py-1">
              <mat-checkbox [ngModel]="item.enabled()" (ngModelChange)="item.enabled.set($event)" />
              <span class="mat-font-body-sm">{{ item.label }}</span>
            </label>
          }
        </div>


      </div>
    }

  </div>
}
